import React, { Component } from "react";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import { WithStyles, Grid, withStyles } from "@material-ui/core";
import cx from "classnames";

import { createStyles, Theme } from "@material-ui/core";

export const paginationStyles = (theme: Theme) =>
  createStyles({
    activeNumber: {
      background: "rgba(29, 125, 234, 0.08)",
      cursor: "pointer",
      color: "#1D7DEA",
      fontSize: 16,
      fontWeight: "bold",
      padding: "0px 8px",
      borderRadius: "50%",
    },
    clickable: {
      cursor: "pointer",
    },
    numberContainer: {
      display: "flex",
      alignItems: "center",
      marginTop: 16,
      "& svg": {
        width: 12,
      },
      "&>span": {
        color: "#1D7DEA",
        fontSize: 16,
        fontWeight: "bold",
        height: 26,
        "&>div:hover": {
          background: "rgba(29, 125, 234, 0.08)",
          cursor: "pointer",
        },
      },
      "&>div": {
        color: "#1D7DEA",
        fontSize: 16,
        fontWeight: "bold",
        padding: "0px 8px",
        borderRadius: "50%",
        "&:hover": {
          background: "rgba(29, 125, 234, 0.08)",
        },
      },
    },
    disableArrow: {
      cursor: "normal",
      opacity: 0.5
    },
  });

interface PaginationProps extends WithStyles<typeof paginationStyles> {
  onPageChange: (currentPage: number, totalPages?: number) => void;
  totalPages: number;
}

interface PaginationState {
  currentPage: number;
  previousPages: number[];
  nextPages: number[];
  lastPage: number | null;
}

class Pagination extends Component<PaginationProps, PaginationState> {
  constructor(props: PaginationProps) {
    super(props);
    this.state = {
      currentPage: 1,
      previousPages: [],
      nextPages: [2, 3, 4, 5],
      lastPage: null,
    };
  }

  handleChangePage() {}

  setCurrentPage = (pageNumber: any) => {
    const { totalPages } = this.props;
    const currentPage = pageNumber;
    this.props.onPageChange(pageNumber, totalPages);
    const prevPages = this.setPrevPages(currentPage);
    const nextPages = this.setNextPages(currentPage);

    this.setState({
      ...this.state,
      currentPage: currentPage,
      previousPages: prevPages,
      nextPages: nextPages,
      lastPage: this.props.totalPages,
    });
  };

  setPrevPages = (currentPage: any) => {
    let previousPages: any = [];

    switch (currentPage) {
      case 1:
        previousPages = [];
        break;
      case currentPage:
        let maxPreviousPages = 2;
        while (currentPage !== 1 && maxPreviousPages !== 0) {
          currentPage = currentPage - 1;
          previousPages.push(currentPage);
          maxPreviousPages -= 1;
        }
        let index = previousPages.indexOf(1);
        if (index > -1) {
          previousPages.splice(index, 1);
        }
        break;
      default:
        previousPages = [];
    }

    return previousPages;
  };

  setNextPages = (currentPage: any) => {
    let nextPages: any = [];
    switch (currentPage) {
      case 1:
        nextPages = [2, 3, 4, 5];
        break;
      case currentPage:
        let maxNextPages = 2;
        while (currentPage !== this.props.totalPages && maxNextPages !== 0) {
          currentPage = currentPage + 1;
          nextPages.push(currentPage);
          maxNextPages -= 1;
        }
        let index = nextPages.indexOf(this.props.totalPages);
        if (index > -1) {
          nextPages.splice(index, 1);
        }
        break;
      default:
        nextPages = [2, 3, 4, 5];
    }

    return nextPages;
  };

  nextPage = (currentPage: any) => {
    let nextPage = currentPage + 1;
    if (nextPage <= this.props.totalPages) {
      this.setCurrentPage(nextPage);
    }
  };

  prevPage = (currentPage: any) => {
    let prevPage = currentPage - 1;
    if (prevPage !== 0) {
      this.setCurrentPage(prevPage);
    }
  };

  generatePaginationElements = () => {
    const { classes, totalPages } = this.props;
    // Add UI
    let firstPage = (
      <Grid
        onClick={() => {
          this.setCurrentPage(1);
        }}
      >
        {1}
      </Grid>
    );
    let currentPage = (
      <Grid className={classes.activeNumber}>{this.state.currentPage}</Grid>
    );
    let morePages = <Grid onClick={() => {}}>...</Grid>;
    let previousPages = this.state.previousPages.map((page, index) => {
      return (
        <Grid
          key={index}
          onClick={() => {
            this.setCurrentPage(page);
          }}
        >
          {page}
        </Grid>
      );
    });
    previousPages = previousPages.reverse();
    let nextPages = this.state.nextPages.map((page, index) => {
      return (
        <Grid
          key={index}
          onClick={() => {
            this.setCurrentPage(page);
          }}
        >
          {page}
        </Grid>
      );
    });

    let lastPage = (
      <Grid
        onClick={() => {
          this.setCurrentPage(this.props.totalPages);
        }}
      >
        {this.props.totalPages}
      </Grid>
    );

    return (
      <span className={classes.numberContainer}>
        <span
          onClick={() => this.prevPage(this.state.currentPage)}
          className={this.state.currentPage === 1 ? classes.disableArrow : classes.clickable}
        >
          {this.state.currentPage > 1?<ArrowBackIosIcon />: <ArrowBackIosIcon color='disabled'/>}
        </span>
        {totalPages <= 5 &&
          Array.from({ length: totalPages }, (_, i) => i + 1).map(
            (pageNumber) => {
              const isActive = pageNumber === this.state.currentPage;
              return (
                <Grid
                  key={pageNumber}
                  className={cx(isActive && classes.activeNumber)}
                  onClick={() => {
                    this.setCurrentPage(pageNumber);
                  }}
                >
                  {pageNumber}
                </Grid>
              );
            }
          )}
        {totalPages > 5 && (
          <>
            {this.state.currentPage !== 1 ? firstPage : null}
            {this.state.currentPage !== 1 && !previousPages.includes(firstPage)
              ? morePages
              : null}
            {previousPages}
            {currentPage}
            {nextPages}
            {this.state.currentPage !== this.state.lastPage &&
            !nextPages.includes(lastPage)
              ? morePages
              : null}
            {this.state.currentPage !== this.state.lastPage ? lastPage : null}
          </>
        )}

        <span
          onClick={() => this.nextPage(this.state.currentPage)}
          className={this.state.currentPage === this.state.lastPage ? classes.disableArrow : classes.clickable }
        >
        {(this.props.totalPages - this.state.currentPage) !== 0 ?<ArrowForwardIosIcon />: <ArrowForwardIosIcon color='disabled'/>}
        </span>
      </span>
    );
  };

  render() {
    const { classes } = this.props;

    const paginationElements = this.generatePaginationElements();
    return (
      <Grid container justify="flex-end" alignItems="center" spacing={24}>
        <Grid item>
          <Grid className={classes.numberContainer}>{paginationElements}</Grid>
        </Grid>
      </Grid>
    );
  }
}

// @ts-ignore
export default withStyles(paginationStyles)(Pagination);
